import {
  useCallback,
  useState,
  MouseEvent,
  Dispatch,
  SetStateAction,
  DOMAttributes,
} from 'react';

export interface SharedHoveredProps
  extends Pick<
    DOMAttributes<HTMLElement>,
    'onMouseEnter' | 'onMouseMove' | 'onMouseLeave'
  > {
  hovered?: boolean;
}

export type ReturnedHoveredProps = SharedHoveredProps &
  Required<Pick<SharedHoveredProps, 'hovered'>>;

export default function useHovered({
  hovered,
  onMouseEnter,
  onMouseLeave,
  onMouseMove,
}: SharedHoveredProps): ReturnedHoveredProps {
  const [hoveredState, setHoveredState]: [
    boolean,
    Dispatch<SetStateAction<boolean>>,
  ] = useState<boolean>(false);

  const handleMouseEnter = useCallback(
    (evt: MouseEvent<HTMLElement>): void => {
      if (onMouseEnter) onMouseEnter(evt);
      setHoveredState(true);
    },
    [onMouseEnter]
  );

  const handleMouseLeave = useCallback(
    (evt: MouseEvent<HTMLElement>): void => {
      if (onMouseLeave) onMouseLeave(evt);
      setHoveredState(false);
    },
    [onMouseLeave]
  );

  // Why listen for `mousemove`? See: https://github.com/HubSpotEngineering/UIComponents/issues/3927
  const handleMouseMove = useCallback(
    (evt: MouseEvent<HTMLElement>): void => {
      if (onMouseMove) onMouseMove(evt);
      setHoveredState(true);
    },
    [onMouseMove]
  );

  return {
    hovered: hovered != null ? hovered : hoveredState,
    onMouseEnter: hoveredState ? onMouseEnter : handleMouseEnter,
    onMouseLeave: hoveredState ? handleMouseLeave : onMouseLeave,
    onMouseMove: hoveredState ? onMouseMove : handleMouseMove,
  };
}
